export type ConnectionStatus = 'idle' | 'checking' | 'online' | 'offline' | 'unavailable' | 'expired';

interface LifecycleOptions {
  document: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'>;
  window: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  isAuthenticated: () => boolean;
  isOnline: () => boolean;
  check: (signal: AbortSignal) => Promise<ConnectionStatus>;
  onStatus: (status: ConnectionStatus) => void;
  onHidden: () => void;
  onReady: () => void;
  timeoutMs?: number;
}

/** Visibility events, not a polling timer, drive HTTP connection checks. */
export function createConnectionLifecycle(options: LifecycleOptions) {
  let away = options.document.hidden;
  let stopped = false;
  let request: AbortController | null = null;
  let inFlight: Promise<void> | null = null;

  const pause = () => {
    away = true;
    request?.abort();
    request = null;
    inFlight = null;
    options.onHidden();
  };
  const check = (): Promise<void> => {
    if (stopped || options.document.hidden || !options.isAuthenticated()) return Promise.resolve();
    if (inFlight) return inFlight;
    if (!options.isOnline()) {
      options.onStatus('offline');
      options.onHidden();
      return Promise.resolve();
    }
    const controller = new AbortController();
    request = controller;
    options.onStatus('checking');
    const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 8000);
    inFlight = (async () => {
      let status: ConnectionStatus;
      try {
        status = await options.check(controller.signal);
      } catch {
        status = options.isOnline() ? 'unavailable' : 'offline';
      } finally {
        clearTimeout(timeout);
      }
      if (request !== controller || stopped || options.document.hidden) return;
      request = null;
      inFlight = null;
      if (!options.isAuthenticated()) return;
      options.onStatus(status);
      if (status === 'online') options.onReady();
      else options.onHidden();
    })();
    return inFlight;
  };
  const restore = () => {
    if (!away || options.document.hidden) return;
    away = false;
    void check();
  };
  const visibility = () => options.document.hidden ? pause() : restore();
  const offline = () => {
    pause();
    options.onStatus('offline');
  };
  const online = () => {
    if (!options.document.hidden) {
      away = false;
      void check();
    }
  };
  const pageshow = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) away = true;
    restore();
  };
  options.document.addEventListener('visibilitychange', visibility);
  options.window.addEventListener('pagehide', pause);
  options.window.addEventListener('pageshow', pageshow);
  options.window.addEventListener('offline', offline);
  options.window.addEventListener('online', online);
  return {
    check,
    stop() {
      stopped = true;
      pause();
      options.document.removeEventListener('visibilitychange', visibility);
      options.window.removeEventListener('pagehide', pause);
      options.window.removeEventListener('pageshow', pageshow);
      options.window.removeEventListener('offline', offline);
      options.window.removeEventListener('online', online);
    },
  };
}
